import * as classnames from "classnames";
import { isEqual } from "lodash";
import * as React from "react";

import { Icon, Modal, Spinner, Msg } from "../../../../shared/components";
import { ChallengeStyle } from "../../../../shared/services/enums";
import Message from "../../../../shared/services/message";

/**
 * Prop interface
 */
export interface SubmitChallengeProps {
  isOpen: boolean;
  hasUnsaved: boolean;
  notLatestExecution: boolean;
  onSubmit: () => void;
  onFinishSubmitChallenge: (type: ChallengeStyle) => void;
  onCancel: () => void;
  type: ChallengeStyle;
  score: number;
  totalScore: number;
  submitting: boolean;
  submitted: boolean;
}

const TOTAL_PATH_LENGTH = 614;

/**
 * Page component
 */
class SubmitChallenge extends React.Component<SubmitChallengeProps> {
  public shouldComponentUpdate(nextProps: SubmitChallengeProps) {
    return !isEqual(nextProps, this.props);
  }

  public render() {
    const {
      isOpen,
      hasUnsaved,
      notLatestExecution,
      onCancel,
      score = 0,
      totalScore = 0,
      type,
      submitting,
      submitted,
    } = this.props;

    const noScored = ChallengeStyle.hasApplicantScore(type) && totalScore === 0;

    const rootStyle = classnames("code-submit-challenge", {
      [`is-submitting`]: submitting,
      [`is-submitted`]: submitted,
      [`no-scored`]: noScored,
      [`not-latest-execution`]: notLatestExecution,
    });

    let mainContents: React.ReactNode = null;
    let footerContents: React.ReactNode = null;

    let okButtonLabel = <Msg id={"button.submitChallenge"} />;

    const submittingPanel = (
      <div className="code-submit-challenge__loading">
        <Spinner className="code-submit-challenge__loading-spinner" />
        <p className="code-submit-challenge__loading-text">
          <Msg id={"submit-challenge.submitting"} />
        </p>
      </div>
    );

    const confirmPanel = (
      <div className="code-submit-challenge__body__contents">
        <Icon type="exclamation-triangle" iconClassName="fa-3x" size="large" />
        <div className="code-submit-challenge__item">
          {hasUnsaved && (
            <div className="code-submit-challenge__unsaved">
              <p>
                <Msg id={"submit-challenge.additional.text1"} />
              </p>
              <p>
                <Msg id={"submit-challenge.additional.text2"} />
              </p>
            </div>
          )}
          <p>
            <Msg id={"submit-challenge.text1"} />
          </p>
        </div>
      </div>
    );

    switch (type) {
      case ChallengeStyle.Quiz:
        if (submitting) {
          mainContents = submittingPanel;
        } else if (submitted) {
          okButtonLabel = <Msg id="button.backToChallengeList" />;
        } else {
          mainContents = (
            <div style={{ margin: "4rem auto" }}>{confirmPanel}</div>
          );
        }
        break;

      case ChallengeStyle.AI:
        if (submitting) {
          mainContents = submittingPanel;
        } else if (submitted) {
          okButtonLabel = <Msg id="submit-challenge.submitted.button" />;
          footerContents = (
            <Msg id={"submit-challenge.coding.submitted.body"} />
          );
        } else {
          mainContents = (
            <div style={{ margin: "4rem auto" }}>{confirmPanel}</div>
          );
        }
        break;

      case ChallengeStyle.Algorithm:
      case ChallengeStyle.Development:
      case ChallengeStyle.Function:
      case ChallengeStyle.Frontend:
        if (submitting) {
          mainContents = submittingPanel;
        } else if (submitted) {
          okButtonLabel = <Msg id="submit-challenge.submitted.button" />;
          footerContents = (
            <Msg id={"submit-challenge.coding.submitted.body"} />
          );
        } else {
          const strokeDashoffset = isOpen
            ? TOTAL_PATH_LENGTH - TOTAL_PATH_LENGTH * (score / totalScore)
            : TOTAL_PATH_LENGTH;

          const scoreMeterPanel = ChallengeStyle.hasApplicantScore(type) && (
            <div className="code-submit-challenge__meter">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                x="0px"
                y="0px"
                viewBox="0 0 288.4 275.5"
              >
                <path
                  className="code-submit-challenge__meter__score-line-behind"
                  d="M52.6,236.5c-51-50.6-51.3-132.9-0.7-183.8S184.8,1.4,235.7,52c51,50.6,51.3,132.9,0.7,183.8c-0.1,0.1-0.3,0.3-0.4,0.4"
                />
                <path
                  className="code-submit-challenge__meter__score-line"
                  style={{ strokeDashoffset: `${strokeDashoffset}px` }}
                  d="M53,238c0,0-38.9-38-38.9-94.2S61.6,14.3,144,14.3S274.2,84,274.2,143.8S236,236.2,236,236.2"
                />
              </svg>
              <div className="code-submit-challenge__meter__score-wrapper">
                <p className="code-submit-challenge__meter__score">{score}</p>
                <p className="code-submit-challenge__meter__total-score">
                  /{totalScore}
                </p>
                <p className="code-submit-challenge__meter__text">
                  {Message.getMessageByKey("submit-challenge.currentScore")}
                </p>
              </div>
            </div>
          );

          const noScoredPanel = noScored && (
            <div className="code-submit-challenge__no-scored">
              <Icon
                type="exclamation-triangle"
                iconClassName="fa-3x"
                size="large"
              />
              <Msg
                id={
                  type === ChallengeStyle.Frontend
                    ? "submit-challenge.vscode.notScored"
                    : "submit-challenge.notScored"
                }
              />
            </div>
          );

          const notLatestExecutionPanel = notLatestExecution && (
            <div className="code-submit-challenge__not-latest-execution">
              <Icon
                type="exclamation-triangle"
                iconClassName="fa-3x"
                size="large"
              />
              <Msg id={"submit-challenge.notLatestExecution"} />
            </div>
          );

          mainContents = (
            <div className="code-submit-challenge__body__contents">
              {scoreMeterPanel}
              {noScoredPanel || notLatestExecutionPanel}
            </div>
          );
        }
        break;

      default:
    }

    return (
      <Modal
        className={rootStyle}
        title={
          <Msg
            id={
              submitted
                ? "submit-challenge.submitted.title"
                : "submit-challenge.title"
            }
          />
        }
        isOpen={isOpen}
        onClose={onCancel}
        onClickCancel={onCancel}
        onClickOk={this.onSubmit}
        hasCancelButton={!submitted}
        okButtonLabel={okButtonLabel}
        additionalFooterContent={footerContents}
        ariaLabel="Submit Challenge"
      >
        <div className="code-submit-challenge__body">{mainContents}</div>
      </Modal>
    );
  }

  private onSubmit = () => {
    if (this.props.submitted) {
      if (typeof this.props.onFinishSubmitChallenge === "function") {
        this.props.onFinishSubmitChallenge(this.props.type);
      }
    } else {
      if (typeof this.props.onSubmit === "function") {
        this.props.onSubmit();
      }
    }
  };
}

export default SubmitChallenge;
